import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Database, BarChart3, Play, Edit, Trash2, Lock, Palette, MoreVertical, Sparkles, Loader2 } from 'lucide-react';
import ProfessionalDashboardForm from '../components/ProfessionalDashboardForm';
import AccessManagementPanel from '../../auth/components/AccessManagementPanel';
import ConfirmDialog from '../../../shared/ui/ConfirmDialog';
import { isAdmin, canManageDashboards, isExternal, isClient } from '../../../utils/permissions';
import toast from 'react-hot-toast';
import { getDashboard } from '../services/dashboards.service';
import type { Dashboard as ApiDashboard } from '../services/dashboards.service';
import { queryKeys } from '../../../shared/services/queryKeys';
import { useDashboardJobStore } from '../../../shared/state/dashboardJobStore';
import { getRealtimeClient } from '../../../shared/services/realtimeClient';

// Mock dashboard for UX/UI development
const MOCK_DASHBOARD: Dashboard = {
  id: 'mock-dashboard-dev',
  name: 'Dashboard Mock',
  description: 'Dashboard de desarrollo con datos mock - Layout editorial editable',
  createdBy: 'Sistema',
  createdAt: 'Desarrollo',
  updatedAt: 'Desarrollo',
  companyId: 'mock',
  empresaId: 'mock',
  widgetsCount: 11,
  assignedTo: [] as any,
  dataSource: { name: 'E-commerce Mock Data (30 transacciones)' } as any
};

type Dashboard = ApiDashboard & {
  companyId?: string | number;
  datasources?: string[];
  prompt?: string | null;
};

type Props = {
  mockDashboards: Dashboard[]; // Renamed from mockDashboards for backwards compatibility
  currentUser: any;
  setSelectedDashboard: (d: Dashboard) => void;
  setCurrentView: (v: string) => void;
  addDashboard?: (d: Partial<Dashboard>) => void;
  updateDashboard?: (id: string, d: Partial<Dashboard>) => void;
  deleteDashboard?: (id: string) => void;
  dashboardsLoading?: boolean;
  dashboardsError?: boolean;
  selectedCompanyId?: string | number;
  companyName?: string;
  companies?: any[];
};

const DashboardsView: React.FC<Props> = ({
  mockDashboards,
  dashboardsLoading = false,
  dashboardsError = false,
  currentUser,
  setSelectedDashboard,
  setCurrentView,
  addDashboard,
  updateDashboard,
  deleteDashboard,
  selectedCompanyId,
  companyName,
  companies,
}) => {
  const dashboards = mockDashboards; // Alias for clarity
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing] = React.useState<Dashboard | undefined>(undefined);
  const [showFiltersPanel, setShowFiltersPanel] = React.useState(false);
  const [selectedDashboardForFilters, setSelectedDashboardForFilters] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<{ isOpen: boolean; dashboardId: string | null }>({ isOpen: false, dashboardId: null });
  const [loadingDashboard, setLoadingDashboard] = React.useState(false);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const {
    jobId,
    status: jobStatus,
    events: jobEvents,
    widgets: jobWidgets,
    error: jobError,
    resetJob,
    applyProgress,
    applyWidget,
    markDone,
    markFailed,
  } = useDashboardJobStore();

  React.useEffect(() => {
    if (!jobId) return;
    const socket = getRealtimeClient();

    const handleProgress = (payload: any) => {
      applyProgress({ event: payload.event, status: payload.status, result: payload.result });
      const data = payload.event?.data;
      if (payload.event?.eventType === 'widget_ready' || payload.event?.stage?.includes('widget')) {
        if (data?.widgetId || data?.title) {
          applyWidget({ id: data?.widgetId, title: data?.title, type: data?.type });
        }
      }
    };

    const handleJobDone = (payload: any) => {
      markDone({ result: payload.result });
      const resultDashboard = payload?.result?.dashboard;
      const resultDashboardId = payload?.result?.dashboardId;
      if (resultDashboard && addDashboard) {
        void addDashboard(resultDashboard);
      } else if (resultDashboardId || !resultDashboard) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
      }
      
      setTimeout(() => {
        resetJob();
      }, 3000);
    };

    const handleJobFailed = (payload: any) => {
      markFailed({ error: payload?.error });
    };

    socket.emit('subscribe_job', { jobId });
    socket.on('job_started', handleProgress);
    socket.on('plan_ready', handleProgress);
    socket.on('widget_ready', handleProgress);
    socket.on('job_progress', handleProgress);
    socket.on('job_done', handleJobDone);
    socket.on('job_failed', handleJobFailed);

    return () => {
      socket.emit('unsubscribe_job', { jobId });
      socket.off('job_started', handleProgress);
      socket.off('plan_ready', handleProgress);
      socket.off('widget_ready', handleProgress);
      socket.off('job_progress', handleProgress);
      socket.off('job_done', handleJobDone);
      socket.off('job_failed', handleJobFailed);
    };
  }, [addDashboard, applyProgress, applyWidget, jobId, markDone, markFailed, queryClient]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const handleEditDashboard = async (dashboard: Dashboard) => {
    setLoadingDashboard(true);
    try {
      // Cargar dashboard completo con todas las relaciones
      const fullDashboard = await queryClient.fetchQuery({
        queryKey: queryKeys.dashboard(dashboard.id),
        queryFn: () => getDashboard(dashboard.id),
      });
      if (!fullDashboard) {
        toast.error('Dashboard no encontrado');
        return;
      }
      
      // Si el dashboard no tiene empresaId, asignar el del usuario actual
      let companyId = fullDashboard.empresaId;
      if (!companyId && currentUser) {
        if (currentUser.userType === 'external') {
          companyId = currentUser.companyId;
        } else if (currentUser.empresaId) {
          companyId = currentUser.empresaId;
        }
        console.log('⚠️ Dashboard sin empresaId, asignando:', companyId);
      }
      
      // Mapear a formato del form
      const formDashboard = {
        ...fullDashboard,
        companyId: companyId,
        datasources: fullDashboard.dataSourceId 
          ? [fullDashboard.dataSourceId, ...(fullDashboard.additionalSources || [])]
          : [],
        prompt: fullDashboard.aiPrompt,
      };
      
      console.log('📝 Dashboard para editar:', {
        id: formDashboard.id,
        name: formDashboard.name,
        companyId: formDashboard.companyId,
        datasources: formDashboard.datasources
      });
      setEditing(formDashboard);
      setShowForm(true);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoadingDashboard(false);
    }
  };
  
  const dashboardCardClass = "group w-full box-border rounded-3xl border-2 border-sky-500/50 bg-gradient-to-br from-slate-900/80 via-slate-950 to-slate-900/70 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.25)] transition-all hover:-translate-y-1 hover:border-sky-400 hover:shadow-[0_20px_50px_rgba(56,189,248,0.35)]";
  const dashboardMetaCardClass = "rounded-2xl border border-sky-800/60 bg-sky-950/50 px-3 py-3";
  const dashboardMetaRowClass = "flex items-center gap-2 rounded-xl border border-sky-800/70 bg-sky-950/60 px-3 py-2";
  const filteredDashboards = dashboards.filter(d =>
    selectedCompanyId ? (d as any).companyId === selectedCompanyId || (d as any).empresaId === selectedCompanyId : true
  );

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-slate-800/80 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 sm:p-8 mb-8">
        <div className="pointer-events-none absolute -left-24 -top-24 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-800/60 px-3 py-1 text-xs font-semibold text-sky-200">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
              Live dashboards
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              Mis Dashboards {companyName ? `— ${companyName}` : ''}
            </h1>
            <p className="text-sm sm:text-base text-slate-300 max-w-xl">
              Gestiona y visualiza tus dashboards ({dashboards.length} dashboards)
            </p>
          </div>
        {(canManageDashboards(currentUser) || (isExternal(currentUser) && isAdmin(currentUser))) && (
          <button
            onClick={() => {
              setEditing(undefined);
              setShowForm(true);
            }}
            className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white px-5 py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-sky-500/30 transition-all hover:-translate-y-0.5"
          >
            <Plus className="w-5 h-5" />
            Nuevo Dashboard
          </button>
        )}
        {showForm && (
          <ProfessionalDashboardForm
            currentUser={currentUser}
            onClose={() => { setShowForm(false); setEditing(undefined); }}
            onSave={async (dashboard: any) => {
              console.log('✅ Professional Dashboard created successfully:', dashboard);
              
              // Close form immediately
              setShowForm(false);
              setEditing(undefined);
              
              // Dashboard is already created by ProfessionalDashboardForm
              // Just update the dashboards list by adding it
              if (addDashboard) {
                // Pass the already created dashboard to update the state
                await addDashboard(dashboard);
              } else {
                console.warn('⚠️ addDashboard function not provided');
              }
            }}
          />
        )}
        </div>
      </div>

      {jobId && jobStatus === 'RUNNING' && (
        <div className="relative mb-6 overflow-hidden rounded-3xl border border-sky-500/30 bg-gradient-to-br from-sky-950/40 via-slate-900/60 to-slate-950/40 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-sky-500/5 via-transparent to-sky-500/5 animate-pulse" />
          <div className="relative px-6 py-5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-500/20 shadow-inner shadow-sky-500/30">
                  <Sparkles className="h-6 w-6 text-sky-400 animate-pulse" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-white mb-1">Creando dashboard</h3>
                  <p className="text-sm text-slate-300">Estamos generando la estructura y widgets automáticamente</p>
                </div>
              </div>
              <button
                onClick={resetJob}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800/50 hover:text-white"
                title="Ocultar"
              >
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
            
            <div className="grid gap-3 sm:grid-cols-3 mb-4">
              {(jobWidgets.length ? jobWidgets : Array.from({ length: 3 }).map((_, index) => ({ id: `skeleton-${index}` }))).map((widget, idx) => {
                const widgetTitle = 'title' in widget ? widget.title : undefined;
                return (
                  <div 
                    key={widget.id} 
                    className="group rounded-2xl border border-sky-800/40 bg-slate-950/60 p-4 transition-all hover:border-sky-700/60"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Loader2 className="h-3.5 w-3.5 text-sky-400 animate-spin" />
                      <div className="h-2 w-16 rounded bg-sky-900/50 animate-pulse" />
                    </div>
                    <div className="h-20 rounded-xl bg-slate-800/50">
                      <div className="h-full w-full animate-pulse rounded-xl bg-gradient-to-br from-slate-700/40 to-slate-800/40" />
                    </div>
                    <p className="mt-3 text-xs text-slate-400">
                      {widgetTitle || 'Preparando widget...'}
                    </p>
                  </div>
                );
              })}
            </div>
            
            {jobEvents.length > 0 && (
              <div className="flex items-center gap-2 rounded-xl border border-sky-900/40 bg-sky-950/30 px-4 py-2.5">
                <div className="flex h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" />
                <p className="text-xs text-slate-300">
                  {jobEvents[jobEvents.length - 1]?.message || 'Procesando...'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {dashboardsLoading && (
        <div className="flex items-center justify-center rounded-3xl border border-slate-800/80 bg-slate-900/60 px-6 py-16 text-slate-200">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
            <p className="text-sm sm:text-base font-medium">Cargando dashboards...</p>
          </div>
        </div>
      )}

      {!dashboardsLoading && dashboardsError && (
        <div className="rounded-3xl border border-red-500/40 bg-red-500/10 px-6 py-12 text-center text-red-200">
          <h2 className="text-lg font-semibold text-red-100">No pudimos cargar los dashboards</h2>
          <p className="mt-2 text-sm text-red-200/80">Intentá refrescar la página o revisá tu conexión.</p>
        </div>
      )}

      {!dashboardsLoading && !dashboardsError && filteredDashboards.length === 0 && !(jobId && jobStatus === 'RUNNING') && (
        <div className="rounded-3xl border border-slate-800/80 bg-gradient-to-br from-slate-900/80 to-slate-950/80 backdrop-blur-sm overflow-hidden">
          <div className="relative px-8 py-16 text-center">
            {/* Decorative background */}
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 via-transparent to-cyan-500/5" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sky-500/20 to-cyan-500/20 rounded-2xl mb-6 ring-1 ring-sky-500/30">
                <BarChart3 className="w-10 h-10 text-sky-400" />
              </div>
              
              {/* Title */}
              <h2 className="text-2xl font-bold text-white mb-3">
                Todavía no tenés dashboards
              </h2>
              
              {/* Description */}
              <p className="text-slate-400 text-base max-w-md mx-auto mb-8">
                Creá tu primer dashboard para empezar a visualizar y analizar tus datos en tiempo real
              </p>

              {/* Secondary Info */}
              <div className="mt-8 pt-8 border-t border-slate-800">
                <p className="text-sm text-slate-500 mb-4">¿Qué podés hacer con dashboards?</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
                  <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                    <div className="text-sky-400 mb-2">📊</div>
                    <h3 className="text-white font-medium text-sm mb-1">Visualizar Métricas</h3>
                    <p className="text-xs text-slate-500">Gráficos y KPIs en tiempo real</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                    <div className="text-cyan-400 mb-2">🎯</div>
                    <h3 className="text-white font-medium text-sm mb-1">Análisis Automatizado</h3>
                    <p className="text-xs text-slate-500">IA genera insights automáticamente</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                    <div className="text-sky-400 mb-2">🔄</div>
                    <h3 className="text-white font-medium text-sm mb-1">Actualización Continua</h3>
                    <p className="text-xs text-slate-500">Datos siempre sincronizados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!dashboardsLoading && !dashboardsError && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div key="mock-dashboard" className={dashboardCardClass}>
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg sm:text-xl font-semibold text-white line-clamp-2">Dashboard Mock</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30">DEV</span>
                  </div>
                  <p className="text-slate-300 text-sm line-clamp-2">Dashboard de desarrollo con datos mock - Layout editorial editable</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-500/20 text-sky-300 shadow-inner shadow-sky-500/30">
                  <BarChart3 className="w-6 h-6" />
                  {/* <Palette className="w-6 h-6" /> */}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 text-xs sm:text-sm">
                <div className={dashboardMetaRowClass}>
                  <Database className="w-4 h-4 text-sky-400" />
                  <span className="text-slate-200">E-commerce Mock Data (30 transacciones)</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div className={dashboardMetaCardClass}>
                  <div className="flex items-center gap-2 text-slate-400">
                    <BarChart3 className="w-4 h-4 text-sky-400" />
                    Widgets
                  </div>
                  <div className="text-lg font-semibold text-white mt-1">11</div>
                </div>
                <div className={dashboardMetaCardClass}>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Edit className="w-4 h-4 text-sky-400" />
                    Editable
                  </div>
                  <div className="text-lg font-semibold text-white mt-1">ON</div>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedDashboard(MOCK_DASHBOARD as any);
                  setCurrentView('viewer');
                }}
                className="w-full bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-sky-500/30"
              >
                <Play className="w-4 h-4" />
                Ver Dashboard Mock
              </button>

              <div className="pt-3 border-t border-sky-800/80">
                <p className="text-xs text-sky-400">Creado por * Gabriel Schvert</p>
                {/* <p className="text-xs text-sky-400">💡 Dashboard para desarrollo UX/UI sin backend</p> */}
              </div>
            </div>
          </div>

          {/* Real dashboards */}
          {filteredDashboards.map((dashboard) => {
            if (!dashboard) return null;
            const isEditableDashboard = canManageDashboards(currentUser) || (isExternal(currentUser) && isClient(currentUser) && dashboard.companyId === currentUser.empresaId);
            return (
          <div key={dashboard.id} className={dashboardCardClass}>
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-1 line-clamp-2">{dashboard.name}</h3>
                  <p className="text-slate-400 text-sm line-clamp-2">{dashboard.description}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-500/20 text-sky-300 shadow-inner shadow-sky-500/30">
                  <BarChart3 className="w-6 h-6" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 text-xs sm:text-sm">
                <div className={dashboardMetaRowClass}>
                  <Database className="w-4 h-4 text-sky-400" />
                  <span className="text-slate-200">{
                    (() => {
                      const hefesto: any = (dashboard as any).hefestoModel;
                      if (hefesto) {
                        const name = typeof hefesto === 'string' ? hefesto : (hefesto.name || '—');
                        const type = typeof hefesto === 'object' && hefesto.type ? ` (${hefesto.type})` : '';
                        return `🔥 ${name}${type}`;
                      }

                      const ds: any = (dashboard as any).datasource ?? (dashboard as any).dataSource ?? (dashboard as any).dataSourceId ?? null;
                      if (!ds) return '—';
                      if (typeof ds === 'string') return ds;
                      if (typeof ds === 'object') return ds.name || ds.titulo || '—';
                      return String(ds);
                    })()
                  }</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div className={dashboardMetaCardClass}>
                  <div className="flex items-center gap-2 text-slate-400">
                    <BarChart3 className="w-4 h-4 text-sky-400" />
                    Widgets
                  </div>
                  <div className="text-lg font-semibold text-white mt-1">{
                    (() => {
                      const wc = (dashboard as any).widgetsCount;
                      if (typeof wc === 'number') return wc;
                      const w = (dashboard as any).widgets;
                      if (Array.isArray(w)) return w.length;
                      if (typeof w === 'number') return w;
                      if (typeof w === 'string' && !isNaN(Number(w))) return Number(w);
                      return 0;
                    })()
                  }</div>
                </div>
                <div className={dashboardMetaCardClass}>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Edit className="w-4 h-4 text-sky-400" />
                    Editable
                  </div>
                  <div className="text-lg font-semibold text-white mt-1">{isEditableDashboard ? 'ON' : 'OFF'}</div>
                </div>
              </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={async () => {
                      try {
                        setLoadingDashboard(true);
                        const full = await queryClient.fetchQuery({
                          queryKey: queryKeys.dashboard(dashboard.id),
                          queryFn: () => getDashboard(dashboard.id),
                        });
                        if (!full) {
                          toast.error('Dashboard no encontrado');
                          return;
                        }
                        const dsId = full.dataSourceId || (full.layout && full.layout.dataSourceId);
                        const hefestoId = full.hefestoModelId;
                        if (!dsId && !hefestoId) {
                          toast.error('Este dashboard no tiene fuente de datos asignada. No se puede abrir.');
                          return;
                        }
                        setSelectedDashboard(full);
                        setCurrentView('viewer');
                      } catch (e) {
                        console.error('Error fetching dashboard before open:', e);
                        toast.error('Error al abrir el dashboard');
                      } finally {
                        setLoadingDashboard(false);
                      }
                    }}
                    className="flex-1 bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Ver
                  </button>
                  {isEditableDashboard && (
                    <div className="relative" ref={openMenuId === dashboard.id ? menuRef : null}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === dashboard.id ? null : dashboard.id)}
                        className="flex items-center justify-center rounded-lg border border-slate-700/70 bg-slate-900/70 px-3 py-2 text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
                        title="Opciones"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {openMenuId === dashboard.id && (
                        <div className="absolute right-0 z-20 mt-2 w-48 rounded-xl border border-slate-700/70 bg-slate-900/95 p-1 shadow-xl">
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              handleEditDashboard(dashboard);
                            }}
                            disabled={loadingDashboard}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800/80 disabled:opacity-50"
                          >
                            <Edit className="h-4 w-4 text-sky-400" />
                            Editar dashboard
                          </button>
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              setConfirmDelete({ isOpen: true, dashboardId: dashboard.id });
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 transition-colors hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4 text-red-400" />
                            Eliminar dashboard
                          </button>
                          {isAdmin(currentUser) && (
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                setSelectedDashboardForFilters(dashboard.id);
                                setShowFiltersPanel(true);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-sky-300 transition-colors hover:bg-sky-500/10"
                            >
                              <Lock className="h-4 w-4 text-sky-400" />
                              Control de acceso
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-sky-800/80">
                  <p className="text-xs text-sky-400">
                    Creado por • {dashboard.createdBy || currentUser?.name || currentUser?.email || 'Usuario'}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Access Management Panel */}
      {showFiltersPanel && selectedDashboardForFilters && (
        <AccessManagementPanel
          dashboardId={selectedDashboardForFilters}
          onClose={() => {
            setShowFiltersPanel(false);
            setSelectedDashboardForFilters(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title="¿Eliminar dashboard?"
        message="Esta acción no se puede deshacer. Se eliminarán todos los widgets y configuraciones asociados a este dashboard."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        onConfirm={() => {
          if (confirmDelete.dashboardId !== null && deleteDashboard) {
            deleteDashboard(confirmDelete.dashboardId);
          }
        }}
        onCancel={() => setConfirmDelete({ isOpen: false, dashboardId: null })}
      />
    </div>
  );
};

export default DashboardsView;
